import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { OvertimeTrendData } from "@/services/att/services/AnalyticsService";
import { useTranslation } from "react-i18next";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

/**
 * Safely format an OT hour value as a 1-decimal string.
 * Backend values may arrive as a number, a numeric string (e.g. "8.5"),
 * or be missing (null / undefined), so coerce with Number() and fall
 * back to "0.0" when the value is not a valid number.
 */
const formatOTHours = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "0.0";
  const num = typeof value === "number" ? value : Number(value);
  return Number.isNaN(num) ? "0.0" : num.toFixed(1);
};

function getOvertimeBarOption(
  data: OvertimeTrendData,
  labels: { t1: string; t2: string; total: string }
) {
  return {
    tooltip: {
      trigger: "axis",
      backgroundColor: "#FFFFFF",
      borderColor: "#E0E0E0",
      borderWidth: 1,
      borderRadius: 8,
      padding: [12, 16],
      textStyle: { color: "#333333", fontSize: 12 },
      formatter: (params: any) => {
        if (!Array.isArray(params) || params.length === 0) return "";
        const idx = params[0]?.dataIndex ?? 0;
        const week = data.weeks[idx] ?? "";

        // One row per series (T1 / T2) using each series' own marker color.
        let total = 0;
        let rows = "";
        params.forEach((p: any) => {
          const num = typeof p.value === "number" ? p.value : Number(p.value);
          const value = Number.isNaN(num) ? 0 : num;
          total += value;
          rows += `<div style="display:flex;justify-content:space-between;gap:30px;font-size:13px;margin-bottom:4px;">
            <span style="color:#666666;font-weight:600;">${p.marker}${p.seriesName}</span>
            <span style="color:#333333;font-weight:700;">${formatOTHours(value)}</span>
          </div>`;
        });

        // Header: week label
        let html = `<div style="font-size:13px;font-weight:600;color:#333333;margin-bottom:8px;">${week}</div>`;
        // T1 / T2 rows
        html += rows;
        // Total row
        html += `<div style="display:flex;justify-content:space-between;gap:30px;font-size:13px;margin-top:6px;padding-top:6px;border-top:1px solid #F0F0F0;">
          <span style="color:#333333;font-weight:700;">${labels.total}</span>
          <span style="color:#FF4D4F;font-weight:700;">${formatOTHours(total)}</span>
        </div>`;
        return html;
      },
    },
    legend: { show: false },
    grid: { left: 45, right: 20, top: 30, bottom: 30 },
    xAxis: {
      type: "category",
      data: data.weeks,
      axisLine: { lineStyle: { color: "#E0E0E0" } },
      axisTick: { show: false },
      axisLabel: { color: "#999999", fontSize: 12 },
    },
    yAxis: {
      type: "value", min: 0, splitNumber: 6,
      axisLine: { show: false }, axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F0F0F0" } },
      axisLabel: { color: "#999999", fontSize: 12 },
    },
    series: [
      {
        name: labels.t1, type: "bar", stack: "total",
        barWidth: 32, barMaxWidth: 32,
        itemStyle: { color: "#3B99FC", borderRadius: [0, 0, 0, 0] },
        data: data.T1Overtime,
        label: { show: false },
      },
      {
        name: labels.t2, type: "bar", stack: "total",
        barWidth: 32, barMaxWidth: 32,
        itemStyle: { color: "#FF6B6B", borderRadius: [0, 0, 4, 4] },
        data: data.T2Overtime,
        label: {
          show: true, position: "top", color: "#333333",
          fontWeight: 600, fontSize: 13,
          formatter: (params: any) => {
            const idx = params.dataIndex;
            return data.T1Overtime[idx] + data.T2Overtime[idx];
          },
        },
      },
    ],
  };
}

interface Props {
  data: OvertimeTrendData | null;
  loading: boolean;
  onRefresh: () => void;
}

const OvertimeTrendChart: React.FC<Props> = ({ data, loading, onRefresh }) => {
  const { t } = useTranslation();
  const labels = {
    t1: t("Dashboard.T1 Overtime"),
    t2: t("Dashboard.T2 Overtime"),
    total: t("Dashboard.Total Overtime"),
  };

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "12px", border: "1px solid #EBEEF2", p: 1.5, display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#333333" }}>
          {t("Dashboard.Overtime Hours Trend")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B99FC" }} />
            <Typography sx={{ fontSize: 12, color: "#999999" }}>{labels.t1}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#FF6B6B" }} />
            <Typography sx={{ fontSize: 12, color: "#999999" }}>{labels.t2}</Typography>
          </Box>
          <RefreshIcon
            sx={{ fontSize: 18, color: "#CCCCCC", cursor: "pointer", ml: 0.5 }}
            onClick={onRefresh}
          />
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : data ? (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <ReactEChartsCore echarts={echarts} option={getOvertimeBarOption(data, labels)} style={{ height: "100%" }} notMerge autoResize />
        </Box>
      ) : null}
    </Box>
  );
};

export default OvertimeTrendChart;
